import { FlexStatus, RecurrenceType } from "@clockwise/schema/v2";
import { useGatewayMutation } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import pluralize from "pluralize";
import toast from "react-hot-toast";
import { logger } from "../../util/logger.util";
import { UpdateEventsFlexibilityDocument } from "../one-on-one-control/graphql/__generated__/UpdateEventsFlexibility.v2.generated";

// Only fully detailed because it's needed by the optimistic response.
type Event = {
  __typename: "Event";
  id: string;
  externalEventId: string;
  title: string;
  timeDescription: string;
  canMarkFlexible: boolean;
  dateOrTimeRange:
    | {
        __typename: "DateRange";
        dateRange: string;
      }
    | {
        __typename: "DateTimeRange";
        timeRange: string;
      };
  recurrence: {
    __typename: "Recurrence";
    type: RecurrenceType;
    description: string | null;
  } | null;
  flexibility: {
    __typename: "FlexDetails";
    isFlexible: boolean;
    status: FlexStatus;
    timeOfDayFlexRange: {
      __typename: "TimeRange";
      start: string;
      end: string;
    } | null;
  } | null;
};

export function useSetFlexibility<E extends Event>(loadedEvents: E[], source: string) {
  const [setFlexibility] = useGatewayMutation(UpdateEventsFlexibilityDocument);
  const trackEvent = useTracking();

  const setFlexibilities = (events: { externalEventId: string; isFlexible: boolean }[]) => {
    const optimisticResponse = events.map(({ externalEventId, isFlexible }) => {
      const currentEvent = loadedEvents.find(({ externalEventId: id }) => id === externalEventId);
      if (!currentEvent || !currentEvent.flexibility) {
        return null;
      }
      return {
        ...currentEvent,
        flexibility: {
          ...currentEvent.flexibility,
          isFlexible,
        },
      };
    });

    void setFlexibility({
      variables: { events },
      optimisticResponse: {
        updateEventsFlexibility: optimisticResponse,
        __typename: "Mutation",
      },
      onCompleted: () => {
        toast.success(`Successfully updated ${pluralize("flexibility", events.length)}`);
        trackEvent(TrackingEvents.FLEXIBLE_MEETING.UPDATE_FLEXIBILITY, {
          events,
          source,
        });
      },
      onError: () => {
        toast.error(`Unable to update ${pluralize("flexibility", events.length)}`);
        logger.error("Failed to update flexibility", { events });
      },
    });
  };

  const handleFlexibilityChange = (eventId: string, isFlexible: boolean) => {
    setFlexibilities([{ externalEventId: eventId, isFlexible }]);
  };

  const handleAllFlexibilityChange = (allFlexible: boolean) => {
    const editableEvents = loadedEvents
      .filter((e) => e.canMarkFlexible)
      .map(({ externalEventId }) => ({ externalEventId, isFlexible: allFlexible }));

    setFlexibilities(editableEvents);
  };

  return {
    handleFlexibilityChange,
    handleAllFlexibilityChange,
  };
}
